import * as React from 'react';
import {
    Typography,
    Box,
    Container,
    Button,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    LinearProgress,
    Chip,
    Alert,
} from '@mui/material';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { centered_flex_box, main_button, sec_button } from '../../app/components/Styles';
import { getTransactionImportTask } from '../../app/store/actions/whFinanceActions';
import { Warning, Receipt, Replay } from '@mui/icons-material';
import { useEffect, useCallback } from 'react';

const STATUS_COLORS = {
    pending: 'warning',
    processing: 'warning',
    success: 'success',
    failed: 'error',
    skipped: 'default',
};

const formatStatus = (status) => {
    if (!status) return 'Unknown';
    return status.charAt(0).toUpperCase() + status.slice(1);
};

const getItemIdentifier = (item) => {
    if (item.tracking_id) return item.tracking_id;
    if (item.order_id) return item.order_id;
    if (item.fincart_order_number) return item.fincart_order_number;
    return '—';
};

const inferTransactionType = (item) => {
    if (item.transactionType) return item.transactionType;
    if (item.fees) return 'ShippingFee';
    if (item.cod) return 'COD';
    if (item.hold) return 'Hold';
    if (item.compensation) return 'Compensation';
    return '—';
};

const getItemValue = (item) => {
    if (item.value != null) return item.value.toFixed(2);
    if (item.cod) return Number(item.cod).toFixed(2);
    if (item.fees) return Number(item.fees).toFixed(2);
    if (item.hold) return Number(item.hold).toFixed(2);
    if (item.compensation) return Number(item.compensation).toFixed(2);
    return '—';
};

export const TransactionImportProgress = ({ getTransactionImportTask }) => {
    const navigate = useNavigate();
    const { taskId } = useParams();
    const [task, setTask] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(true);
    const [pollError, setPollError] = React.useState(null);

    const fetchTask = useCallback(async () => {
        try {
            const data = await getTransactionImportTask(taskId);
            setTask(data);
            setPollError(null);
        } catch (err) {
            setPollError(err?.response?.data?.message || 'Failed to load import progress.');
        } finally {
            setIsLoading(false);
        }
    }, [getTransactionImportTask, taskId]);

    useEffect(() => {
        fetchTask();
    }, [fetchTask]);

    useEffect(() => {
        if (!task || task.status !== 'processing') {
            return undefined;
        }

        const interval = setInterval(() => {
            fetchTask();
        }, 3000);

        return () => clearInterval(interval);
    }, [task?.status, fetchTask]);

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (task?.status === 'processing') {
                event.preventDefault();
                event.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [task?.status]);

    const summary = task?.summary || {};
    const items = task?.items || [];
    const completedCount = (summary.success || 0) + (summary.failed || 0) + (summary.skipped || 0);
    const progressPercent = summary.total > 0 ? Math.round((completedCount / summary.total) * 100) : 0;
    const isProcessing = task?.status === 'processing';

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
                <Typography variant="h4" textAlign="center" sx={{ color: 'var(--secColor)', mb: 1 }}>
                    Transaction Import Progress
                </Typography>
                {task?.source && (
                    <Typography variant="body1" textAlign="center" color="text.secondary" sx={{ mb: 1 }}>
                        Source: {task.source}
                    </Typography>
                )}
                {task?.financeUser?.name && (
                    <Typography variant="body2" textAlign="center" color="text.secondary" sx={{ mb: 2 }}>
                        Started by {task.financeUser.name} · {task.createdAt ? new Date(task.createdAt).toLocaleString() : ''}
                    </Typography>
                )}

                {isProcessing && (
                    <Alert severity="warning" icon={<Warning />} sx={{ mb: 3 }}>
                        Do not close this page until the import finishes. Transactions are processing in the background and may take several minutes for large files.
                    </Alert>
                )}

                {task?.status === 'completed' && (
                    <Alert severity={summary.failed > 0 ? 'warning' : 'success'} sx={{ mb: 3 }}>
                        Import completed. {summary.success} succeeded, {summary.failed} failed, {summary.skipped} skipped.
                    </Alert>
                )}

                {task?.status === 'failed' && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        The import task failed unexpectedly. Some transactions may not have been recorded.
                    </Alert>
                )}

                {pollError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {pollError} — will retry on next poll.
                    </Alert>
                )}

                {isLoading && !task ? (
                    <Box sx={{ ...centered_flex_box, py: 6 }}>
                        <CircularProgress sx={{ color: 'var(--secColor)' }} />
                    </Box>
                ) : task ? (
                    <>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                            <Chip label={`Total: ${summary.total || 0}`} />
                            <Chip label={`Success: ${summary.success || 0}`} color="success" />
                            <Chip label={`Failed: ${summary.failed || 0}`} color="error" />
                            <Chip label={`Skipped: ${summary.skipped || 0}`} />
                            <Chip label={`Pending: ${summary.pending || 0}`} color="warning" />
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Overall progress
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {progressPercent}%
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={progressPercent}
                                sx={{ height: 10, borderRadius: 5 }}
                            />
                            {isProcessing && (
                                <Box sx={{ ...centered_flex_box, mt: 2, gap: 1 }}>
                                    <CircularProgress size={20} sx={{ color: 'var(--secColor)' }} />
                                    <Typography variant="body2" color="text.secondary">
                                        Processing transactions...
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        <TableContainer sx={{ maxHeight: 500 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Identifier</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell align="right">Value</TableCell>
                                        <TableCell>Cashout ID</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Reason</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {items.map((item, index) => (
                                        <TableRow
                                            key={`${getItemIdentifier(item)}-${index}`}
                                            sx={{
                                                bgcolor: item.status === 'failed'
                                                    ? 'error.light'
                                                    : item.status === 'success'
                                                        ? 'success.light'
                                                        : item.status === 'processing'
                                                            ? 'warning.light'
                                                            : 'inherit',
                                            }}
                                        >
                                            <TableCell>{getItemIdentifier(item)}</TableCell>
                                            <TableCell>{inferTransactionType(item)}</TableCell>
                                            <TableCell align="right">{getItemValue(item)}</TableCell>
                                            <TableCell>{item.cashoutId || '—'}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={formatStatus(item.status)}
                                                    color={STATUS_COLORS[item.status] || 'default'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="caption"
                                                    color={item.status === 'failed' ? 'error' : 'text.secondary'}
                                                >
                                                    {item.reason || ''}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {!isProcessing && (
                            <Box sx={{ ...centered_flex_box, gap: 2, mt: 3, flexWrap: 'wrap' }}>
                                <Button
                                    sx={{ ...sec_button }}
                                    startIcon={<Replay />}
                                    onClick={() => navigate('/warehousing/finance/transactions/add')}
                                >
                                    Import Another
                                </Button>
                                <Button
                                    sx={{ ...main_button }}
                                    startIcon={<Receipt />}
                                    onClick={() => navigate('/warehousing/finance/transactions')}
                                >
                                    View Transactions
                                </Button>
                            </Box>
                        )}
                    </>
                ) : null}
            </Paper>
        </Container>
    );
};

const mapDispatchToProps = { getTransactionImportTask };

export default connect(null, mapDispatchToProps)(TransactionImportProgress);
